import moment from 'moment'
import { type UserScorecardHistoryPoint } from '../../../shared/insights/store/insights'

export type SeriesPoint = [number, number]

export interface WeeklyPoint {
  startTimestamp: number
  endTimestamp: number
  average: number
  meetingCount: number
  min: number
  max: number
}

export interface MonthlyPoint {
  month: string
  average: number
  meetingCount: number
  min: number
  max: number
}

const sortByDate = (points: UserScorecardHistoryPoint[]): UserScorecardHistoryPoint[] =>
  [...points].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

// The 90 day view plots one point per week, meeting-weighted averaging that week's daily scores
export const toWeeklyPoints = (points: UserScorecardHistoryPoint[]): WeeklyPoint[] => {
  const sorted = sortByDate(points)
  if (sorted.length === 0) {
    return []
  }
  const firstDay = moment(sorted[0].date).startOf('day')
  const buckets = new Map<number, UserScorecardHistoryPoint[]>()
  sorted.forEach((point) => {
    const index = Math.floor(moment(point.date).diff(firstDay, 'days') / 7)
    buckets.set(index, [...(buckets.get(index) ?? []), point])
  })
  return [...buckets.entries()]
    .sort(([a], [b]) => a - b)
    .map(([index, bucket]) => {
      const meetingCount = bucket.reduce((sum, point) => sum + point.meeting_count, 0)
      return {
        startTimestamp: firstDay
          .clone()
          .add(index * 7, 'days')
          .valueOf(),
        endTimestamp: firstDay
          .clone()
          .add(index * 7 + 6, 'days')
          .valueOf(),
        average: Math.round(bucket.reduce((sum, point) => sum + point.score * point.meeting_count, 0) / meetingCount),
        meetingCount,
        min: Math.min(...bucket.map((point) => point.min_score)),
        max: Math.max(...bucket.map((point) => point.max_score)),
      }
    })
}

// The 6/12 month views plot one point per calendar month
export const toMonthlyPoints = (points: UserScorecardHistoryPoint[]): MonthlyPoint[] => {
  const sorted = sortByDate(points)
  const buckets = new Map<string, UserScorecardHistoryPoint[]>()
  sorted.forEach((point) => {
    const key = moment(point.date).format('YYYY-MM')
    buckets.set(key, [...(buckets.get(key) ?? []), point])
  })
  return [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, bucket]) => {
      const meetingCount = bucket.reduce((sum, point) => sum + point.meeting_count, 0)
      return {
        month,
        average: Math.round(bucket.reduce((sum, point) => sum + point.score * point.meeting_count, 0) / meetingCount),
        meetingCount,
        min: Math.min(...bucket.map((point) => point.min_score)),
        max: Math.max(...bucket.map((point) => point.max_score)),
      }
    })
}

export const toWeeklySeries = (points: WeeklyPoint[]): SeriesPoint[] =>
  points.map((point) => [Math.round((point.startTimestamp + point.endTimestamp) / 2), point.average])

export const formatMonthCategory = (month: string): string => moment(month, 'YYYY-MM').format('MMM [’]YY')

// Day-level axis labels for the 90 day view, where month labels would repeat across ticks
export const formatAxisDay = (timestamp: number): string => moment(timestamp).format('MMM D')

export const formatWeekRange = (point: WeeklyPoint): string => {
  const start = moment(point.startTimestamp)
  const end = moment(point.endTimestamp)
  if (start.isSame(end, 'month')) {
    return `${start.format('D')}-${end.format('D')} ${end.format('MMMM, YYYY')}`
  }
  return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`
}

export const formatMonthHeading = (month: string): string => moment(month, 'YYYY-MM').format('MMMM YYYY')
